import React, { useCallback, useEffect, useState } from "react";
import { KeyRound, Laptop, LogOut, Mail, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { ActionIcon } from "../components/ActionIcon";
import { PasswordChecklist, isPasswordValid } from "../components/PasswordChecklist";
import { PasswordField } from "../components/PasswordField";
import { useAuth } from "../lib/auth";

const when = (iso) =>
  new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });

/** Trusted devices, sign-out-everywhere, and step-up protected password / email changes. */
export default function AdminSecurity() {
  const { user } = useAuth();
  const personal = [user?.name, user?.username, user?.work_email, user?.email];
  const [devices, setDevices] = useState([]);
  const [sessions, setSessions] = useState(0);
  const [trustHours, setTrustHours] = useState(24);
  const [sso, setSso] = useState(null);
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [newEmail, setNewEmail] = useState("");
  const [purpose, setPurpose] = useState("");
  const [stepUpToken, setStepUpToken] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    api.get("/admin/sso/credential").then(({ data }) => setSso(data)).catch(() => setSso(null));
    const { data } = await api.get("/auth/devices");
    setDevices(data.devices);
    setSessions(data.active_sessions);
    setTrustHours(data.trust_window_hours);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, [load]);

  const remove = async (device) => {
    if (!window.confirm(`Remove ${device.user_agent}? That browser will need a new email code next time.`)) return;
    try {
      await api.delete(`/auth/devices/${device.id}`);
      toast.success("Device removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const revokeAll = async () => {
    if (!window.confirm("Sign out of all devices? You will need to sign in again here too.")) return;
    try {
      await api.post("/auth/devices/revoke-all");
      toast.success("Signed out everywhere");
      window.location.href = "/login";
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const requestCode = async (which) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/step-up/request", { purpose: which });
      setStepUpToken(data.step_up_token);
      setPurpose(which);
      setCode("");
      toast.success(`Verification code sent to ${data.email_hint}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmChange = async (e) => {
    e.preventDefault();
    if (purpose === "password" && form.new_password !== form.confirm) {
      toast.error("Both passwords must match");
      return;
    }
    setBusy(true);
    try {
      const { data: grantData } = await api.post("/auth/step-up/verify", {
        step_up_token: stepUpToken, code,
      });
      if (purpose === "password") {
        await api.post("/auth/change-password", {
          current_password: form.current_password, new_password: form.new_password,
          grant: grantData.grant,
        });
        toast.success("Password changed. Sign in again.");
      } else {
        await api.post("/auth/change-email", { new_email: newEmail, grant: grantData.grant });
        toast.success("Email updated. Sign in again.");
      }
      window.location.href = "/login";
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-10 lg:px-10" data-testid="admin-security">
      <div className="eyebrow mb-2">Account security</div>
      <h1 className="font-heading font-black text-4xl md:text-5xl leading-none tracking-tight mb-3" style={{ color: "var(--ink)" }}>
        Security
      </h1>
      <p className="text-sm max-w-2xl mb-10" style={{ color: "var(--muted)" }}>
        Devices you have verified skip the email code for {trustHours} hours. Changing your password
        or email, or signing out of all devices, removes every trusted device and ends every session.
        You have {sessions} active session(s).
      </p>

      {sso?.enabled && (
        <div className="border p-6 mb-10" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="sso-credential-card">
          <div className="eyebrow mb-3">Microsoft 365 sign-in</div>
          <div className="grid gap-4 md:grid-cols-4 text-sm">
            <div>
              <div className="eyebrow text-[9px]">Credential</div>
              <div className="mt-1" style={{ color: "var(--ink)" }}>
                {sso.credential === "certificate" ? "Certificate" : "Client secret"}
              </div>
            </div>
            <div>
              <div className="eyebrow text-[9px]">Expires</div>
              <div className="mt-1" style={{ color: "var(--ink)" }} data-testid="sso-cert-expiry">
                {sso.expires_at
                  ? new Date(sso.expires_at).toLocaleDateString("en-US", { dateStyle: "medium" })
                  : "—"}
                {typeof sso.days_remaining === "number" && (
                  <span className="block text-xs" style={{ color: sso.days_remaining < 60 ? "#B45309" : "var(--muted)" }}>
                    {sso.days_remaining} days remaining
                  </span>
                )}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="eyebrow text-[9px]">Thumbprint (SHA-256)</div>
              <div className="mt-1 font-mono text-[11px] break-all" style={{ color: "var(--muted)" }}>
                {sso.thumbprint || "—"}
              </div>
            </div>
          </div>
          <p className="mt-4 text-xs" style={{ color: "var(--muted)" }}>
            Administrators may sign in with Microsoft 365 instead of a password; Entra's own
            verification replaces the portal's email code. Requiring Microsoft sign-in, emergency
            accounts and certificate rotation are managed under System → SSO &amp; Emergency Access.
          </p>
        </div>
      )}

      <h2 className="font-heading font-bold text-2xl mb-4 flex items-center gap-2" style={{ color: "var(--ink)" }}>
        <Laptop size={18} style={{ color: "var(--muted)" }}/> Trusted devices
      </h2>
      <div className="border max-h-[420px] overflow-auto mb-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="devices-table-region">
        <table className="w-full text-sm min-w-[640px]" data-testid="devices-table">
          <thead>
            <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
              <th className="px-4 py-3">Device</th>
              <th className="px-4 py-3">Last seen IP</th>
              <th className="px-4 py-3">Trusted at</th>
              <th className="px-4 py-3">Expires</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => (
              <tr key={device.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`device-row-${device.id}`}>
                <td className="px-4 py-3 font-medium" style={{ color: "var(--ink)" }}>
                  <span className="flex items-center gap-2">
                    {device.user_agent}
                    {device.current && <span className="pill text-[9px]" data-testid="device-current">This device</span>}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--muted)" }}>{device.last_seen_ip || "—"}</td>
                <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{when(device.created_at)}</td>
                <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{when(device.expires_at)}</td>
                <td className="px-4 py-3 text-right">
                  <ActionIcon icon={Trash2} label="Remove this device" danger onClick={() => remove(device)} testId={`remove-device-${device.id}`}/>
                </td>
              </tr>
            ))}
            {!devices.length && (
              <tr><td colSpan={5} className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="devices-empty">
                No trusted devices. Every sign-in asks for an email code.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <button onClick={revokeAll} className="btn-ghost flex items-center gap-2 mb-12" data-testid="revoke-all-devices">
        <LogOut size={14}/> Sign out of all devices
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="border p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
          <h3 className="font-heading font-bold text-lg mb-1 flex items-center gap-2" style={{ color: "var(--ink)" }}>
            <KeyRound size={16} style={{ color: "var(--muted)" }}/> Change password
          </h3>
          <p className="text-xs mb-5" style={{ color: "var(--muted)" }}>
            At least 12 characters with an uppercase letter, a lowercase letter, a number and a
            symbol. Breached passwords are rejected. We email a fresh code before saving.
          </p>
          <div className="space-y-5">
            <div>
              <label className="field-label">Current password</label>
              <PasswordField value={form.current_password}
                onChange={(e) => setForm({ ...form, current_password: e.target.value })} testId="sec-current-password"/>
            </div>
            <div>
              <label className="field-label">New password</label>
              <PasswordField minLength={12} value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })} testId="sec-new-password"/>
            </div>
            <div>
              <label className="field-label">Confirm new password</label>
              <PasswordField minLength={12} value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })} testId="sec-confirm-password"/>
              <PasswordChecklist
                password={form.new_password}
                confirm={form.confirm}
                personal={personal}
                testId="sec-password-checklist"
              />
            </div>
            <button onClick={() => requestCode("password")} disabled={busy || !isPasswordValid(form.new_password, { confirm: form.confirm, personal })} className="btn-primary flex items-center gap-2" data-testid="sec-request-password-code">
              <ShieldCheck size={14}/> Email me a code
            </button>
          </div>
        </div>

        <div className="border p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
          <h3 className="font-heading font-bold text-lg mb-1 flex items-center gap-2" style={{ color: "var(--ink)" }}>
            <Mail size={16} style={{ color: "var(--muted)" }}/> Change work email
          </h3>
          <p className="text-xs mb-5" style={{ color: "var(--muted)" }}>
            The code goes to your current address. After the change you will sign in again.
          </p>
          <div className="space-y-5">
            <div>
              <label className="field-label">New work email</label>
              <input className="input-flat" type="email" value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)} data-testid="sec-new-email"/>
            </div>
            <button onClick={() => requestCode("email")} disabled={busy || !newEmail} className="btn-primary flex items-center gap-2" data-testid="sec-request-email-code">
              <ShieldCheck size={14}/> Email me a code
            </button>
          </div>
        </div>
      </div>

      {purpose && (
        <form onSubmit={confirmChange} className="border p-6 mt-8 max-w-md" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="step-up-form">
          <h3 className="font-heading font-bold text-lg mb-1" style={{ color: "var(--ink)" }}>
            Confirm with your code
          </h3>
          <p className="text-xs mb-5" style={{ color: "var(--muted)" }}>
            Enter the 6-digit code we just emailed you to finish changing your{" "}
            {purpose === "password" ? "password" : "email address"}. It expires in 10 minutes.
          </p>
          <input className="input-flat font-mono tracking-[0.4em]" inputMode="numeric" maxLength={6}
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            data-testid="step-up-code"/>
          <button type="submit" disabled={busy || code.length !== 6} className="btn-primary mt-5 flex items-center gap-2" data-testid="step-up-confirm">
            <ShieldCheck size={14}/> {busy ? "Saving…" : "Confirm change"}
          </button>
        </form>
      )}
    </div>
  );
}
